import { useState, useEffect, useCallback } from "react";
import { useAction } from "convex/react";
import { api } from "convex/_generated/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Link } from "wouter";
import { Check, Loader2, Search } from "lucide-react";
import { getTodayISO } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { DanceStyleSelect } from "./DanceStyleSelect";
import { TagSelect } from "./TagSelect";
import { TimeSelect, followMeridiem } from "./TimeSelect";
import { useLateNightTag } from "./useLateNightTag";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { VenueSelector } from "./VenueSelector";
import { RecurrenceSelector, defaultRecurrenceConfig, toRecurrenceArgs } from "./RecurrenceSelector";
import type { RecurrenceConfig } from "./RecurrenceSelector";
import { LessonSection } from "./LessonSection";
import type { LessonValue } from "./LessonSection";
import { PriceSection, toPriceArgs, priceError, DEFAULT_PRICE_UNIT } from "./PriceSection";
import type { PriceValue } from "./PriceSection";
import { FlyerUpload } from "./FlyerUpload";
import type { Id } from "convex/_generated/dataModel";

const STORAGE_KEY = "nedances-event-draft";

interface FormData {
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime: string;
  venueName: string;
  venueAddress: string;
  venueId: string | null;
  isManualVenue: boolean;
  eventUrl: string;
  danceStyles: string[];
  mainStyles: string[];
  tags: string[];
  submittedBy: string;
  recurrenceConfig: RecurrenceConfig | null;
  lesson: LessonValue | null;
  price: PriceValue | null;
  flyerStorageId: Id<"_storage"> | null;
}

const createInitialFormData = (): FormData => ({
  title: "",
  description: "",
  date: getTodayISO(),
  startTime: "19:00",
  endTime: "",
  venueName: "",
  venueAddress: "",
  venueId: null,
  isManualVenue: false,
  eventUrl: "",
  danceStyles: [],
  mainStyles: [],
  tags: [],
  submittedBy: "",
  recurrenceConfig: null,
  lesson: null,
  price: null,
  flyerStorageId: null,
});

function loadDraft(): FormData {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<FormData> & Record<string, unknown>;
      // Update date if it's in the past
      const date = typeof parsed.date === "string" && parsed.date >= getTodayISO()
        ? parsed.date
        : getTodayISO();
      const initial = createInitialFormData();
      return {
        ...initial,
        ...parsed,
        date,
        // Migrate old isRecurring field — drop legacy recurring fields
        recurrenceConfig: parsed.recurrenceConfig
          ? {
              ...parsed.recurrenceConfig,
              // Drafts saved before multi-week monthlies have no ordinals.
              ordinals: parsed.recurrenceConfig.ordinals ?? [],
            }
          : null,
        // Likewise for drafts saved before the price had a unit — a missing
        // one would otherwise reach the form as undefined and throw.
        price: parsed.price
          ? { ...parsed.price, unit: parsed.price.unit ?? DEFAULT_PRICE_UNIT }
          : null,
      } as FormData;
    }
  } catch {
    // Ignore parse errors
  }
  return createInitialFormData();
}

function saveDraft(data: FormData) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

export function EventSubmitForm() {
  const [formData, setFormData] = useState<FormData>(createInitialFormData);
  const [venueCoords, setVenueCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedStatus, setSubmittedStatus] = useState<"approved" | "pending" | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const { isLoggedIn: isSignedIn } = useCurrentUser();
  const submitEvent = useAction(api.events.submitEventWithGeocode);
  const createRecurring = useAction(api.recurring.createRecurringEventWithGeocode);

  // Load draft from localStorage on mount
  useEffect(() => {
    setFormData(loadDraft());
    setIsLoaded(true);
  }, []);

  // Save draft to localStorage on changes (after initial load)
  useEffect(() => {
    if (isLoaded) {
      saveDraft(formData);
    }
  }, [formData, isLoaded]);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // An end time past midnight tags the event Late Night without being asked.
  const setTags = useCallback(
    (tags: string[]) => setFormData((prev) => ({ ...prev, tags })),
    [],
  );
  useLateNightTag(formData.startTime, formData.endTime, formData.tags, setTags);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.date) {
      newErrors.date = "Date is required";
    }
    if (!formData.startTime) {
      newErrors.startTime = "Start time is required";
    }
    if (formData.danceStyles.length === 0) {
      newErrors.danceStyles = "Select at least one dance style";
    }
    if (!formData.venueName.trim()) {
      newErrors.venueName = "Venue name is required";
    }
    if (!formData.recurrenceConfig) {
      if (!formData.submittedBy.trim()) {
        newErrors.submittedBy = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.submittedBy)) {
        newErrors.submittedBy = "Enter a valid email address";
      }
    }

    const priceMessage = priceError(formData.price);
    if (priceMessage) {
      newErrors.price = priceMessage;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;
    if (formData.recurrenceConfig && !isSignedIn) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (formData.recurrenceConfig) {
        const cfg = formData.recurrenceConfig;
        const { frequency, dayOfWeek, dayOfMonth, ordinals } = toRecurrenceArgs(
          cfg,
          formData.date
        );

        const recurringResult = await createRecurring({
          lessonStartTime: formData.lesson?.startTime || undefined,
          lessonEndTime: formData.lesson?.endTime || undefined,
          ...toPriceArgs(formData.price),
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          startTime: formData.startTime,
          endTime: formData.endTime || undefined,
          venueName: formData.venueName.trim(),
          venueAddress: formData.venueAddress.trim() || undefined,
          venueId: (formData.venueId ?? undefined) as Id<"venues"> | undefined,
          eventUrl: formData.eventUrl.trim() || undefined,
          danceStyles: formData.danceStyles,
          mainStyles: formData.mainStyles,
          tags: formData.tags.length > 0 ? formData.tags : undefined,
          venueLatitude: venueCoords?.latitude,
          venueLongitude: venueCoords?.longitude,
          flyerStorageId: formData.flyerStorageId ?? undefined,
          frequency,
          interval: cfg.interval,
          dayOfWeek,
          dayOfMonth,
          ordinals,
          firstEventOverride: cfg.firstEventDiffers && cfg.firstEventDate ? cfg.firstEventDate : undefined,
          startDate: formData.date,
          endDate: cfg.endDate || undefined,
        });
        setSubmittedStatus(recurringResult.status);
      } else {
        const singleResult = await submitEvent({
          lessonStartTime: formData.lesson?.startTime || undefined,
          lessonEndTime: formData.lesson?.endTime || undefined,
          ...toPriceArgs(formData.price),
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          date: formData.date,
          startTime: formData.startTime,
          endTime: formData.endTime || undefined,
          venueName: formData.venueName.trim(),
          venueAddress: formData.venueAddress.trim() || undefined,
          venueId: (formData.venueId ?? undefined) as Id<"venues"> | undefined,
          eventUrl: formData.eventUrl.trim() || undefined,
          danceStyles: formData.danceStyles,
          mainStyles: formData.mainStyles,
          tags: formData.tags.length > 0 ? formData.tags : undefined,
          submittedBy: formData.submittedBy.trim(),
          venueLatitude: venueCoords?.latitude,
          venueLongitude: venueCoords?.longitude,
          flyerStorageId: formData.flyerStorageId ?? undefined,
        });
        setSubmittedStatus(singleResult.status);
      }

      setIsSuccess(true);
      clearDraft();
      setFormData(createInitialFormData());
      setVenueCoords(null);
    } catch (error: any) {
      console.error("Failed to submit event:", error);
      // Surface server-side validation messages (e.g. venue conflicts) directly
      const msg: string =
        error?.message ?? error?.data?.message ?? String(error);
      setSubmitError(
        msg && msg !== "[object Object]"
          ? msg
          : "Something went wrong submitting your event. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    const approved = submittedStatus === "approved";
    return (
      <div className="text-center py-12 animate-in fade-in zoom-in-95 duration-500">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${approved ? "bg-green-100" : "bg-yellow-100"}`}>
          <Check className={`w-8 h-8 ${approved ? "text-green-600" : "text-yellow-600"}`} />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {approved ? "Event Published!" : "Event Submitted!"}
        </h2>
        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
          {approved
            ? "Your event is now live and visible to everyone on the calendar."
            : "Your event has been submitted and is pending review. It will appear on the calendar once approved by a moderator."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {approved && (
            <Button asChild>
              <Link href="/events">View Calendar</Link>
            </Button>
          )}
          <Button variant={approved ? "outline" : "default"} onClick={() => { setIsSuccess(false); setSubmittedStatus(null); }}>
            Submit Another Event
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <FormField label="Event Title" required error={errors.title}>
        <Input
          value={formData.title}
          onChange={(e) => updateField("title", e.target.value)}
          placeholder="e.g., Latin Social Night"
          aria-invalid={!!errors.title}
        />
      </FormField>

      {/* Date, then the times. The times keep a row to themselves: an hour, a
          minute and AM/PM need more width than a third of one. */}
      <FormField label="Date" required error={errors.date}>
        <Input
          type="date"
          value={formData.date}
          onChange={(e) => updateField("date", e.target.value)}
          min={getTodayISO()}
          aria-invalid={!!errors.date}
          className="sm:max-w-xs"
        />
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Start Time" required error={errors.startTime}>
          <TimeSelect
            value={formData.startTime}
            onChange={(v) => updateField("startTime", v)}
            placeholder="Start time"
          />
        </FormField>
        <FormField label="End Time">
          <TimeSelect
            value={formData.endTime}
            onChange={(v) => updateField("endTime", v)}
            placeholder="End time"
            defaultMeridiem={followMeridiem(formData.startTime)}
          />
        </FormField>
      </div>

      {/* Every venue on the site is in New England, so times are stored and
          shown as Eastern rather than converted per reader. Saying so is what
          stops someone entering their own timezone's clock. */}
      <p className="-mt-3 text-xs text-muted-foreground">
        All times are Eastern (ET).
      </p>

      {/* Lesson */}
      <LessonSection
        eventStartTime={formData.startTime}
        value={formData.lesson}
        onChange={(v) => updateField("lesson", v)}
      />

      {/* Price */}
      <PriceSection
        value={formData.price}
        onChange={(v) => updateField("price", v)}
        error={errors.price}
      />

      {/* Recurring event */}
      <RecurrenceSelector
        value={formData.recurrenceConfig}
        onChange={(cfg) => updateField("recurrenceConfig", cfg)}
        startDate={formData.date}
      />
      {!isSignedIn && formData.recurrenceConfig && (
        <p className="text-xs text-amber-500">
          You need to be signed in to submit recurring events.
        </p>
      )}

      {/* Dance Styles */}
      <FormField label="Dance Styles" required error={errors.danceStyles}>
        <DanceStyleSelect
          value={formData.danceStyles}
          onChange={(v) => updateField("danceStyles", v)}
          mainStyles={formData.mainStyles}
          onMainStylesChange={(v) => updateField("mainStyles", v)}
        />
      </FormField>

      {/* Tags */}
      <FormField label="Event Tags">
        <TagSelect
          value={formData.tags}
          onChange={(v) => updateField("tags", v)}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Select any that apply — these help dancers find the right fit.
        </p>
      </FormField>

      {/* Venue */}
      <FormField label="Venue" required error={errors.venueName}>
        {formData.isManualVenue ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  value={formData.venueName}
                  onChange={(e) => updateField("venueName", e.target.value)}
                  placeholder="e.g., Dance Studio ABC"
                  aria-invalid={!!errors.venueName}
                />
              </div>
              <div>
                <AddressAutocomplete
                  value={formData.venueAddress}
                  onChange={(address) => {
                    updateField("venueAddress", address);
                    setVenueCoords(null);
                  }}
                  onPlaceSelect={(place) => {
                    updateField("venueAddress", place.address);
                    setVenueCoords(place.coords);
                  }}
                  placeholder="e.g., 123 Main St, Austin, TX"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => updateField("isManualVenue", false)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <Search className="w-3 h-3" />
              Search venues instead
            </button>
          </>
        ) : (
          <VenueSelector
            value={formData.venueId as Id<"venues"> | null}
            venueName={formData.venueName}
            venueAddress={formData.venueAddress}
            onSelect={(venue) => {
              setFormData((prev) => ({
                ...prev,
                venueId: venue.id,
                venueName: venue.name,
                venueAddress: venue.address,
              }));
              setVenueCoords(
                venue.latitude !== undefined && venue.longitude !== undefined
                  ? { latitude: venue.latitude, longitude: venue.longitude }
                  : null
              );
              if (errors.venueName) {
                setErrors((prev) => ({ ...prev, venueName: undefined }));
              }
            }}
            onClear={() => {
              setFormData((prev) => ({
                ...prev,
                venueId: null,
                venueName: "",
                venueAddress: "",
              }));
              setVenueCoords(null);
            }}
            onManualEntry={(query) => {
              setFormData((prev) => ({
                ...prev,
                isManualVenue: true,
                venueName: query,
              }));
            }}
          />
        )}
      </FormField>

      {/* Event URL */}
      <FormField label="Event URL">
        <Input
          type="url"
          value={formData.eventUrl}
          onChange={(e) => updateField("eventUrl", e.target.value)}
          placeholder="https://..."
        />
      </FormField>

      {/* Description */}
      <FormField label="Description">
        <Textarea
          value={formData.description}
          onChange={(e) => updateField("description", e.target.value)}
          placeholder="Tell people about this event..."
          rows={4}
        />
      </FormField>

      {/* Flyer */}
      <FormField label="Event Flyer">
        <FlyerUpload
          isPublic
          storageId={formData.flyerStorageId}
          onUpload={(id) => updateField("flyerStorageId", id)}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Optional — upload a flyer or poster image for this event.
        </p>
      </FormField>

      {/* Email — only needed for one-off events (recurring uses account email) */}
      {!formData.recurrenceConfig && (
        <FormField label="Your Email" required error={errors.submittedBy}>
          <Input
            type="email"
            value={formData.submittedBy}
            onChange={(e) => updateField("submittedBy", e.target.value)}
            placeholder="you@example.com"
            aria-invalid={!!errors.submittedBy}
          />
          <p className="text-xs text-muted-foreground mt-1">
            We'll use this to contact you if we have questions about your event.
          </p>
        </FormField>
      )}

      {/* Submit */}
      {submitError && (
        <p className="text-sm text-destructive">{submitError}</p>
      )}

      {formData.recurrenceConfig && !isSignedIn ? (
        <Button asChild size="lg" className="w-full">
          <a href="/login">Sign in to Submit Recurring Event</a>
        </Button>
      ) : (
        <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : formData.recurrenceConfig ? (
            "Submit Recurring Event"
          ) : (
            "Submit Event"
          )}
        </Button>
      )}
    </form>
  );
}

interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

function FormField({ label, required, error, children }: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label className={cn(error && "text-destructive")}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {children}
      {error && (
        <p className="text-sm text-destructive animate-in fade-in slide-in-from-top-1 duration-200">
          {error}
        </p>
      )}
    </div>
  );
}
